import {
  AddRounded,
  AddToDriveOutlined,
  AssignmentOutlined,
  CloudSyncOutlined,
  FilterDramaOutlined,
  NoteAddOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import { lazy, Suspense, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { isNil } from "lodash";
import LoadingFallback from "../../../components/LoadingFallback";
import ConfirmationDialog from "../../../components/ui/ConfirmationDialog";
import { useEnv } from "../../../hooks/useEnv";
import { useLoanTermsHook } from "../../../hooks/useLoanTermsHook";
import { useReduxHook } from "../../../hooks/useReduxHook";
import { useReduxLoanDrilldownHook } from "../../../hooks/useReduxLoanDrilldownHook";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { sidebarReset } from "../../../redux/actions/sidebarActions";
import { grey } from "@mui/material/colors";

// Lazy load heavy components
const DropdownButton = lazy(
  () => import("../../../components/ui/buttons/DropdownButton"),
);
const LoanBulkFileUploader = lazy(
  () => import("../shared/LoanBulkFileUploader"),
);

const TYPES = {
  SALESFORCE_SYNC: "salesforce-sync",
  BOX_SYNC: "box-sync",
};

const LoanSyncDropdownButton = () => {
  const dispatch = useDispatch();
  const [modalType, setModalType] = useState(null);
  // const [showUpload, setShowUpload] = useState(false);
  const { isAdmin } = useReduxHook();
  // const { isLendmarq } = useEnv();
  // const [statusSync, setStatusSync] = useState(false);
  const { isResfin } = useEnv();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const handleUploadClick = useCallback((type) => {
    setModalType(type);
  }, []);

  const options = [
    {
      label: "Create Salesforce Opportunity",
      // onClick: () => setStatusSync(true),
      onClick: () => handleUploadClick(TYPES.SALESFORCE_SYNC),

      icon: <AddRounded sx={{ color: grey[900] }} />,
    },
    {
      label: loanDrilldown?.sync_loan_id
        ? "Salesforce Integration Status Sync"
        : "Salesforce Integration Status Sync (External ID Missing)",
      // onClick: () => setStatusSync(true),
      onClick: () => handleUploadClick(TYPES.SALESFORCE_SYNC),
      disabled: isNil(loanDrilldown?.sync_loan_id),
      icon: <CloudSyncOutlined className="thin" />,
    },
    {
      label: "Sync With Box",
      onClick: () => handleUploadClick(TYPES.BOX_SYNC),
      disabled: true,
      icon: <AssignmentOutlined className="thin" />,
      // icon: <AddToDriveOutlined className="thin" />,
    },
  ];

  if (!isResfin) {
    return null;
  }

  if (isResfin || isAdmin) {
    return (
      <>
        <Suspense fallback={<LoadingFallback />}>
          {modalType === TYPES.SALESFORCE_SYNC && (
            <SalesforceSyncConfirmationModal
              onClose={() => setModalType(null)}
            />
          )}

          {modalType === TYPES.BOX_SYNC && (
            <BoxSyncConfirmationModal onClose={() => setModalType(null)} />
          )}

          <DropdownButton
            size="small"
            // size="large"
            endIcon={<SyncOutlined />}
            variant="outlined"
            options={options}
          >
            External Sync
          </DropdownButton>
        </Suspense>
      </>
    );
  }

  // Only show if admin
  return;
};

const SalesforceSyncConfirmationModal = ({ onClose }) => {
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, syncLoanWithSalesforceById } = useLoanTermsHook();
  const { getLoanById } = useUnderwritingHook();
  const dispatch = useDispatch();

  const handleRefresh = () => {
    clearLoanDrilldownData();
    // dispatch(loanDrilldownClear());
    getLoanById({
      loanId: loanDrilldown?._id,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        // dispatch(sidebarReset());
      },
      onFailFn: () => dispatch(loanDrilldownSet({})),
    });
  };

  const onSubmit = () => {
    syncLoanWithSalesforceById({
      dealId: loanDrilldown?._id,
      onSuccessFn: () => {
        handleRefresh();
        onClose();
      },
    });
  };
  return (
    <ConfirmationDialog
      open
      maxWidth="xs"
      loading={loading}
      onCancel={onClose}
      onConfirm={onSubmit}
      title={"Salesforce Status Sync"}
      message={
        isNil(loanDrilldown?.sync_loan_id) ? (
          "Are you sure you want to create an Opportunity in Salesforce for this loan?"
        ) : (
          <>
            Are you sure you want to sync this loan with Salesforce? The
            associated Salesforce Loan ID is{" "}
            <b>{loanDrilldown?.sync_loan_id}</b>.
          </>
        )
      }
      confirmText="Yes, Sync With Salesforce"
    />
  );
};

const BoxSyncConfirmationModal = ({ onClose }) => {
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, syncLoanWithSalesforceById } = useLoanTermsHook();
  const { getLoanById } = useUnderwritingHook();
  const dispatch = useDispatch();

  const handleRefresh = () => {
    clearLoanDrilldownData();
    // dispatch(loanDrilldownClear());
    getLoanById({
      loanId: loanDrilldown?._id,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        // dispatch(sidebarReset());
      },
      onFailFn: () => dispatch(loanDrilldownSet({})),
    });
  };

  const onSubmit = () => {
    return;
    syncLoanWithSalesforceById({
      dealId: loanDrilldown?._id,
      onSuccessFn: () => {
        handleRefresh();
        onClose();
      },
    });
  };
  return (
    <ConfirmationDialog
      open
      maxWidth="xs"
      loading={loading}
      onCancel={onClose}
      onConfirm={onSubmit}
      title={"Sync With Box"}
      message={
        <>
          Are you sure you want to sync this loan with Box? The associated Box
          Loan ID is <b>{loanDrilldown?.sync_loan_id}</b>.
        </>
      }
      confirmText="Yes, Sync With Salesforce"
    />
  );
};

export default LoanSyncDropdownButton;
